import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDeal } from "../lib/store";
import { Card, ConfirmModal, ComposeEmailModal, useCompose, AddToCampaignModal, useOutreach } from "../components/shared";
import { useRoomShare } from "../components/ShareRoomModal";
import { capitalApi, shareApi, instantlyApi } from "../lib/api";
import { csvCell } from "../lib/utils";
import { toast } from "sonner";
import * as m from "../lib/model";
import { RaiseTiles, RaiseActivity, FunnelCard, FollowUpsCard, ReadinessCard, SourcesCard, STAGES } from "../components/capital/Scoreboard";
import { CapitalRail } from "../components/capital/CapitalRail";
import { LeadModal, LP_TYPES } from "../components/capital/LeadModal";
import { LeadRow } from "../components/capital/LeadRow";
import { LeadDetail } from "../components/capital/LeadDetail";
import { InvestorListSection } from "../components/capital/InvestorListSection";
import { RaiseTimeline } from "../components/capital/RaiseTimeline";
import { TeamChips } from "../components/team/TeamChips";
import { TeamSection } from "../components/team/TeamSection";
import { DealSummary } from "../components/capital/DealSummary";
import DebtMarkets from "../components/debt/DebtMarkets";
import { investorsApi, authApi } from "../lib/api";
import { UserPlus, Download, RefreshCcw, Users, Send, Share2, Globe, Trash2, ChevronLeft, ChevronRight, PiggyBank, Landmark, Inbox, CheckCheck } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const PAGE = 100;
const CSV_COLS = ["name", "firm", "type", "email", "phone", "stage", "interest", "askAmount", "indicated", "committed",
  "funded", "classPref", "accredited", "docsStatus", "source", "referrer", "owner", "lastContact", "nextFollowUp", "notes"];

export default function CapitalMarkets() {
  const { M, scenario, scope: dashScope, dealName, navigate } = useDeal();
  const { share: room } = useRoomShare();
  const compose = useCompose();
  const outreach = useOutreach();
  const platformName = dealName || M.dealName || "this platform";
  const dealKey = M.dealKey || "";
  const rollup = m.scopeOptions(M) ? m.rollupScopedModel(M) : null;
  // Mirror the Dashboard: pull the exact same scope model + active case (base/upside/downside),
  // so every figure here ties out to what the Dashboard shows for the equivalent view.
  const platformOnly = useMemo(() => m.scenarioModel(m.scopedModel(M, m.SCOPE_PLATFORM), scenario), [M, scenario]);
  const rollupModel = useMemo(() => m.scenarioModel(m.scopedModel(M, m.SCOPE_ROLLUP), scenario), [M, scenario]);
  // Match the Investor Room's ask exactly: the LP cash ask (lpAskOf), honouring any room override —
  // NOT lpEquityBase, which also counts rolled legacy equity that is not raised from LPs.
  const roomAsk = room && Number(room.equityAsk) > 0 ? Number(room.equityAsk) : 0;
  const dealTarget = roomAsk || m.lpAskOf(platformOnly) || 0;
  const platformTarget = roomAsk || m.lpAskOf(rollupModel) || dealTarget;

  // Open on whichever view the Dashboard is currently showing.
  const [scope, setScope] = useState(dashScope === m.SCOPE_ROLLUP ? "platform" : "deal");
  // Model behind the summary / emails: consolidated roll-up when the platform scope is picked
  const VM = scope === "platform" ? rollupModel : platformOnly;
  const [mode, setMode] = useState("equity");
  const [sel_, setSelSection] = useState("scoreboard");
  const [debtSection, setDebtSection] = useState("dscoreboard");
  const [addLender, setAddLender] = useState(0);
  const switchMode = (mo) => { setMode(mo); window.scrollTo({ top: 0 }); document.querySelector(".main")?.scrollTo?.({ top: 0 }); };
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tl, setTl] = useState(null);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("All");
  const [type, setType] = useState("All");
  const [owner, setOwner] = useState("All");
  const [users, setUsers] = useState([]);
  const [due, setDue] = useState("");
  const [sel, setSel] = useState({});
  const [shown, setShown] = useState(PAGE);
  const [modal, setModal] = useState({ open: false, initial: null });
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState("");
  const [contactsTotal, setContactsTotal] = useState(0);
  const [replies, setReplies] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [folder, setFolder] = useState("All");
  const [eng, setEng] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const today = todayStr();

  const target = scope === "deal" ? dealTarget : scope === "platform" ? platformTarget : 0;
  const params = useMemo(() => (scope === "all" ? {} : { dealKey, dealName: platformName }), [scope, dealKey, platformName]);

  const load = useCallback(async () => {
    const [rows, s] = await Promise.all([
      capitalApi.list({ ...params, limit: 50000, ...(type !== "All" ? { type } : {}) }),
      capitalApi.summary({ ...params, target, ...(type !== "All" ? { type } : {}) }),
    ]);
    setLeads(rows);
    setSummary(s);
  }, [params, target, type]);

  useEffect(() => { load().catch(() => toast.error("Could not load the pipeline")); }, [load]);
  useEffect(() => {
    if (scope === "all") { setTl(null); return; }
    capitalApi.timeline({ dealKey, dealName: platformName }).then(setTl).catch(() => setTl(null));
  }, [scope, dealKey, platformName, leads]);
  useEffect(() => { investorsApi.stats().then((s) => setContactsTotal(s.total || 0)).catch(() => { }); authApi.users().then(setUsers).catch(() => { }); }, []);
  useEffect(() => {
    setSubject(`Investment Opportunity — ${platformName}`);
    setBody(`Following up on ${platformName}${VM.addons ? ` (platform + ${VM.addons.length} add-ons)` : ""}.\n\nAdj. EBITDA: ${m.fmt$(VM.addons ? VM.sde + VM.addons.reduce((a, c) => a + c.ebitda, 0) : VM.sde)}\n${VM.addons ? "Total capital deployed" : "Purchase price"}: ${m.fmt$(VM.addons ? m.sourcesUses(VM).totalUses : VM.askPrice)}\nLP equity raise: ${m.fmt$(target || dealTarget)}\nModeled LP MOIC: ${m.fmtX(m.lpWaterfall(VM).lpMoic)} after a ${VM.prefReturn}% preferred return\n\nHappy to walk through the model and the deal room — let me know a time this week.`);
  }, [platformName, M.sde, M.askPrice, dealTarget, M.prefReturn]); // eslint-disable-line

  const folders = useMemo(() => {
    const names = new Set(leads.map((l) => l.folder).filter(Boolean));
    return [...names].sort().concat(leads.some((l) => !l.folder) ? ["Unfiled"] : []);
  }, [leads]);

  const filtered = useMemo(() => {
    const week = new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
    return leads.filter((l) => {
      if (stage !== "All" && l.stage !== stage) return false;
      if (folder !== "All" && (l.folder || "Unfiled") !== folder) return false;
      if (owner === "Unassigned" ? !!l.owner : owner !== "All" && l.owner !== owner) return false;
      if (eng === "campaign" && !l.inCampaign) return false;
      if (eng === "hot" && (l.interest || "").toLowerCase() !== "hot") return false;
      if (eng === "new" && (l.inCampaign || (l.interest || "").toLowerCase() === "hot")) return false;
      if (q && ![l.name, l.firm, l.email, l.source, l.referrer].join(" ").toLowerCase().includes(q.toLowerCase())) return false;
      const d = l.nextFollowUp || "";
      if (due === "overdue" && !(d && d < today)) return false;
      if (due === "today" && d !== today) return false;
      if (due === "week" && !(d && d > today && d <= week)) return false;
      return true;
    });
  }, [leads, q, stage, folder, due, today, owner, eng]);

  const page = filtered.slice(0, shown);
  const expandedLead = leads.find((l) => l.id === expandedId) || null;
  const picked = leads.filter((l) => sel[l.id]);
  const refreshSummary = async () => setSummary(await capitalApi.summary({ ...params, target, ...(type !== "All" ? { type } : {}) }));
  const replace = (lead) => { setLeads((ls) => ls.map((l) => (l.id === lead.id ? lead : l))); refreshSummary(); };

  const save = async (form) => {
    if (form.id) { replace(await capitalApi.patch(form.id, form)); toast.success("LP updated"); }
    else { await capitalApi.create(form); await load(); toast.success(`${form.name} added to the pipeline`); }
  };
  const remove = async (lead) => {
    await capitalApi.remove(lead.id);
    setLeads((ls) => ls.filter((l) => l.id !== lead.id));
    refreshSummary();
    toast.success(`${lead.name} removed`);
  };
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const r = await fn(); await load(); toast.success(msg(r)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };
  const requirePicked = () => {
    if (!picked.length) { toast.error("Tick at least one LP in the LP pipeline section"); return false; }
    return true;
  };
  const toCampaign = async () => {
    if (!requirePicked()) return;
    const { contacts } = await capitalApi.outreachContacts({ ids: picked.map((l) => l.id) });
    outreach.start(contacts, `${platformName} — LP raise`);
  };
  const selectNotInCampaign = () => {
    const targets = filtered.filter((l) => !l.inCampaign);
    if (!targets.length) { toast.info("Every LP matching these filters is already in a campaign"); return; }
    setSel(Object.fromEntries(targets.map((l) => [l.id, true])));
    toast.success(`${targets.length} LP(s) not yet in a campaign selected`);
  };
  const bulkStage = async (stage) => {
    if (!stage) return;
    if (!requirePicked()) return;
    const ids = picked.map((l) => l.id);
    setBusy("stage");
    try { await capitalApi.bulkPatch(ids, { stage }); await load(); toast.success(`Moved ${ids.length} LP(s) to “${stage}”`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update stage"); }
    finally { setBusy(""); }
  };
  useEffect(() => {
    instantlyApi.inbox({ unread_only: true, limit: 100 }).then((r) => setReplies(r.unread || 0)).catch(() => {});
  }, []);
  const openCompose = () => {
    if (!requirePicked()) return;
    compose.start({
      title: "Email LPs — AI drafted follow-up",
      recipients: picked.map((l) => ({ name: l.name, email: l.email })),
      subject: subject || `Investment Opportunity — ${platformName}`,
      body, kind: "investors", dealName: platformName, M,
      vars: [["Deal name", platformName], ["LP raise", m.fmt$(target || dealTarget)], ["Adj. EBITDA", m.fmt$(VM.sde)], ["LP MOIC", m.fmtX(m.lpWaterfall(VM).lpMoic)]],
      draftEndpoint: "/ai/outreach",
      draftPayload: { deal: M, investor: { name: picked[0]?.firm || picked[0]?.name || "Investor", firm: picked[0]?.firm || "", type: picked[0]?.type || "LP", contact: picked.length === 1 ? (picked[0]?.contact || picked[0]?.name || "") : "{{firstName}}" } },
    });
  };
  const openInvite = () => {
    if (!requirePicked()) return;
    if (!room) { toast.error("Publish the Deal Room first"); return navigate("dealroom"); }
    const wf = m.lpWaterfall(VM);
    compose.start({
      title: "Invite LPs to the Deal Room",
      recipients: picked.map((l) => ({ name: l.name, email: l.email })),
      subject: `${room.name || platformName} — Investor Deal Room access`,
      body: `Hi {{firstName}},\n\nYou're invited to review **${room.name || platformName}** in our secure investor deal room — live underwriting, documents and a hypothetical investor calculator.\n\n**Deal Room:** ${room.roomUrl || `${window.location.origin}/room/${room.id}`}\n**Passcode:** ${room.passcode}\n\nWe are raising **${m.fmt$(target || dealTarget)}** of LP equity (modeled ${m.fmtX(wf.lpMoic)} MOIC after a ${M.prefReturn}% preferred return). You can indicate a non-binding soft commitment inside the room.\n\nThe link and passcode are personal to you — please do not forward.`,
      kind: "room-invite", dealName: platformName, roomId: room.id, M,
      vars: [["Deal room link", room.roomUrl || `${window.location.origin}/room/${room.id}`], ["Passcode", room.passcode], ["LP raise", m.fmt$(target || dealTarget)], ["Personal link (skips the passcode)", "{{roomLink}}"]],
      onSent: (to) => shareApi.invites(room.id, to).catch(() => { }),
    });
  };
  const quickSend = async () => {
    if (!requirePicked()) return;
    const withEmail = picked.filter((l) => l.email);
    if (!withEmail.length) return toast.error("None of the selected LPs have an email address");
    setBusy("quick");
    try {
      const res = await Promise.all(withEmail.map((l) => capitalApi.sendEmail(l.id, { subject, body })));
      const ok = res.filter((r) => r.delivered).length;
      toast[ok ? "success" : "info"](ok ? `Sent to ${ok}/${withEmail.length} LP(s) and logged on each record` : "Logged on each record (no live email — Resend key missing)");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(""); }
  };
  const bulkDelete = async () => {
    const ids = picked.map((l) => l.id);
    await capitalApi.bulkDelete({ ids });
    setSel({});
    await load();
    toast.success(`${ids.length} LP(s) removed`);
  };
  const exportCsv = () => {
    const rows = filtered.map((l) => CSV_COLS.map((c) => csvCell(l[c])).join(","));
    const blob = new Blob([[CSV_COLS.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `capital-pipeline-${scope}.csv`;
    a.click();
  };
  const jumpFollowUps = (k) => { setDue(due === k ? "" : k); setSelSection("pipeline"); };
  const allChecked = page.length > 0 && page.every((l) => sel[l.id]);

  const sections = [
    { id: "scoreboard", label: "Raise Activity" },
    { id: "summary", label: "Deal Summary" },
    { id: "timeline", label: "Raise Timeline", badge: tl ? `${tl.daysToClose}d` : null },
    { id: "funnel", label: "Conversion Funnel" },
    { id: "followups", label: "Follow-ups Due", badge: summary?.followUps?.overdue || null },
    { id: "readiness", label: "Close Readiness" },
    { id: "sources", label: "Top Sources" },
    { id: "pipeline", label: "LP Pipeline", badge: leads.length || null },
    { id: "investors", label: "Investor List", badge: contactsTotal ? contactsTotal.toLocaleString() : null },
    { id: "team", label: "Team & Activity" },
    { id: "outreach", label: "Outreach & Email" },
  ];
  const idx = sections.findIndex((x) => x.id === sel_);
  const cur = sections[idx] || sections[0];
  const s = summary;

  return (
    <div className="page" data-testid="page-capital">
      <div className="page-head cm-head">
        <div>
          <div className="page-title">Capital Markets</div>
          <div className="page-sub">{mode === "equity" ? "Fundraising pipeline — investor interest, ticket sizes, follow-ups and the path to a closed raise" : "Debt pipeline — lender term sheets, comparison, closing timeline and the path to funded"}</div>
          {scope !== "all" && <div style={{ marginTop: 8 }}><TeamChips scope="raise" dealKey={dealKey} dealName={platformName} label={mode === "equity" ? "Fundraising team" : "Debt team"} compact /></div>}
        </div>
        <div className="cm-actions">
          {replies > 0 && (
            <button className="cm-replies-chip" onClick={() => navigate("inbox")} title="Open the Outreach Inbox" data-testid="cm-replies-badge">
              <Inbox size={13} />{replies} new {replies === 1 ? "reply" : "replies"}
            </button>
          )}
          <div className="cm-mode" role="tablist" data-mode={mode} data-testid="cm-mode">
            <button className={`cm-mode-btn ${mode === "equity" ? "on" : ""}`} onClick={() => switchMode("equity")} data-testid="cm-mode-equity"><span className="cm-mode-k">A</span><PiggyBank size={13} />Equity</button>
            <button className={`cm-mode-btn ${mode === "debt" ? "on" : ""}`} onClick={() => switchMode("debt")} data-testid="cm-mode-debt"><span className="cm-mode-k">B</span><Landmark size={13} />Debt</button>
          </div>
          <select className="inp cm-scope-sel" value={scope} onChange={(e) => { setScope(e.target.value); setSel({}); }} data-testid="cm-scope">
            <option value="deal">{`This deal — ${platformName}`}</option>
            <option value="platform">{`${platformName} + add-ons combined${rollup ? ` (${rollup.addons.length} add-on${rollup.addons.length > 1 ? "s" : ""})` : ""}`}</option>
            <option value="all">{"All deals — firm-wide roster"}</option>
          </select>
          {mode === "equity" && <button className="btn btn-primary" onClick={() => setModal({ open: true, initial: null })} data-testid="cm-add-lp"><UserPlus size={13} />Add LP</button>}
          {mode === "debt" && scope !== "all" && <button className="btn btn-primary" onClick={() => setAddLender((n) => n + 1)} data-testid="cm-add-lender"><Landmark size={13} />Add lender</button>}
        </div>
      </div>

      {mode === "debt" && <div key="debt" className="cm-pane" data-testid="cm-pane-debt">{scope === "all"
        ? <Card title="Debt pipeline" testid="debt-all-scope"><div className="cm-empty">Lender pipelines are per deal — pick a deal in the scope selector above.</div></Card>
        : <DebtMarkets dealKey={dealKey} dealName={platformName} section={debtSection} onSection={setDebtSection} addSignal={addLender} />}</div>}

      {mode === "equity" && <div key="equity" className="asmp-layout cm-pane" data-testid="cm-pane-equity">
        <CapitalRail s={s} tl={tl} sections={sections} active={sel_} onSelect={setSelSection} />
        <div className="asmp-body" data-testid="cm-section-body">
          <div className="asmp-crumb">
            <span className="ac-idx">{idx + 1} / {sections.length}</span>
            <span className="ac-lbl">{cur.label}</span>
            <div className="ac-nav">
              <button className="btn" disabled={idx <= 0} onClick={() => setSelSection(sections[idx - 1].id)} data-testid="cm-prev"><ChevronLeft size={13} />Prev</button>
              <button className="btn" disabled={idx >= sections.length - 1} onClick={() => setSelSection(sections[idx + 1].id)} data-testid="cm-next">Next<ChevronRight size={13} /></button>
            </div>
          </div>

          {!s && <Card title="Loading raise data" testid="cm-loading"><div className="cm-empty">Fetching the pipeline…</div></Card>}

          {s && sel_ === "scoreboard" && <RaiseActivity s={s} tl={tl} leads={leads} onOpenTimeline={() => setSelSection("timeline")} />}
          {sel_ === "summary" && <DealSummary M={VM} s={s} target={target || dealTarget} tl={tl} room={room} />}
          {sel_ === "timeline" && (scope === "all"
            ? <Card title="Raise timeline" testid="tl-all-scope"><div className="cm-empty">Timelines are per deal — pick a deal in the scope selector above to plan its raise.</div></Card>
            : <RaiseTimeline dealKey={dealKey} dealName={platformName} target={target} tl={tl} onChange={setTl} />)}
          {s && sel_ === "funnel" && <FunnelCard s={s} />}
          {s && sel_ === "followups" && <FollowUpsCard s={s} onJump={jumpFollowUps} />}
          {s && sel_ === "readiness" && <ReadinessCard s={s} />}
          {s && sel_ === "sources" && <SourcesCard s={s} />}

          {sel_ === "investors" && (
            <InvestorListSection dealKey={dealKey} dealName={platformName} tracked={leads} onImported={load} />
          )}

          {sel_ === "team" && (scope === "all"
            ? <Card title="Team & activity" testid="team-all-scope"><div className="cm-empty">Teams are assigned per deal — pick a deal in the scope selector above.</div></Card>
            : <TeamSection dealKey={dealKey} dealName={platformName} />)}

          {sel_ === "outreach" && (
            <Card title="Outreach — email the selected LPs" testid="cm-outreach">
              <div className="cm-out-grid">
                <div>
                  <div className="field"><label>Subject</label>
                    <input className="inp inp-text" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="cm-subject" /></div>
                  <div className="field"><label>Body</label>
                    <textarea className="inp" rows={9} value={body} onChange={(e) => setBody(e.target.value)} data-testid="cm-body" /></div>
                </div>
                <div className="cm-out-side">
                  <div className="cm-out-count" data-testid="cm-recipients">{picked.length} recipient(s) selected</div>
                  <button className="btn btn-primary" onClick={openCompose} data-testid="cm-compose">Compose Email + AI Draft</button>
                  <button className="btn" onClick={quickSend} disabled={busy === "quick"} data-testid="cm-quick-send"><Send size={13} />{busy === "quick" ? "Sending…" : "Quick Send"}</button>
                  <button className="btn btn-share" onClick={openInvite} data-testid="cm-invite-room" title={room ? `Room ${room.id}` : "Publish the Deal Room first"}>
                    <Globe size={13} />Invite to Deal Room{room ? "" : " (not published)"}
                  </button>
                  <button className="btn" onClick={toCampaign} data-testid="cm-campaign"><Share2 size={13} />Add to campaign ({picked.length})</button>
                  <button className="btn" onClick={() => (picked.length ? setConfirm({ bulk: true, name: `${picked.length} LPs` }) : requirePicked())} data-testid="cm-bulk-delete"><Trash2 size={13} />Remove selected</button>
                  <div className="cm-empty">Tick LPs in the LP Pipeline section first. Every send is logged on that LP's timeline, and Deal Room soft-commits land here automatically — cutting the remaining raise.</div>
                </div>
              </div>
            </Card>
          )}

          {sel_ === "pipeline" && (
            <Card title={`LP pipeline — ${filtered.length} of ${leads.length} LPs`} testid="cm-pipeline">
              <div className="cm-toolbar">
                <input className="inp cm-search" placeholder="Search name, firm, source…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cm-search" />
                <select className="inp cm-inline" value={stage} onChange={(e) => setStage(e.target.value)} data-testid="cm-stage-filter">
                  {["All", ...STAGES].map((x) => <option key={x} value={x}>{x === "All" ? "All stages" : x}</option>)}
                </select>
                <select className="inp cm-inline" value={type} onChange={(e) => setType(e.target.value)} data-testid="cm-type-filter">
                  {["All", ...LP_TYPES].map((t) => <option key={t} value={t}>{t === "All" ? "All investor types" : t}</option>)}
                </select>
                <select className="inp cm-inline" value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="cm-folder-filter">
                  {["All", ...folders].map((f) => <option key={f} value={f}>{f === "All" ? "All folders" : f}</option>)}
                </select>
                <select className="inp cm-inline" value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="cm-owner-filter">
                  <option value="All">All team</option>
                  <option value="Unassigned">Unassigned</option>
                  {users.map((u) => <option key={u.id} value={u.name || u.email}>{u.name || u.email}</option>)}
                </select>
                <select className="inp cm-inline" value={due} onChange={(e) => setDue(e.target.value)} data-testid="cm-due-filter">
                  <option value="">All follow-ups</option>
                  <option value="overdue">Overdue</option>
                  <option value="today">Due today</option>
                  <option value="week">Next 7 days</option>
                </select>
                <button className="btn" onClick={() => run("room", () => capitalApi.syncRoom({ dealKey, dealName: platformName, altKeys: [] }), (r) => r.detail || `Deal Room synced — ${r.added} added, ${r.updated} updated`)} disabled={busy === "room" || scope === "all"} data-testid="cm-sync-room"><RefreshCcw size={13} />{busy === "room" ? "Syncing…" : "Sync Deal Room"}</button>
                <button className="btn" onClick={() => run("imp", () => capitalApi.importInvestors({ dealKey, dealName: platformName, type: type === "All" ? null : type, limit: 300 }), (r) => `Imported ${r.imported} LPs (${r.skipped} already tracked)`)} disabled={busy === "imp"} data-testid="cm-import-investors"><Users size={13} />{busy === "imp" ? "Importing…" : `Import Investors${type !== "All" ? ` (${type})` : ""}`}</button>
                <button className="btn" onClick={() => run("dig", () => capitalApi.digest({ days: 7, ...(scope === "all" ? {} : { dealKey }) }), (r) => (r.count ? `Chase list for ${r.count} LP(s) ${r.delivered ? `emailed to ${r.to}` : "ready (email key missing)"}` : "Nothing due in the next 7 days"))} disabled={busy === "dig"} data-testid="cm-digest"><Send size={13} />{busy === "dig" ? "Sending…" : "Email chase list"}</button>
                <button className="btn" onClick={exportCsv} data-testid="cm-export"><Download size={13} />CSV</button>
                <button className="btn" onClick={selectNotInCampaign} data-testid="cm-select-new" title="Select only LPs not yet added to a campaign"><CheckCheck size={13} />Select new</button>
                <select className="inp cm-inline" value="" onChange={(e) => bulkStage(e.target.value)} disabled={!picked.length || busy === "stage"} data-testid="cm-bulk-stage" title="Set stage for the selected LPs">
                  <option value="">{busy === "stage" ? "Updating…" : `Set stage${picked.length ? ` (${picked.length})` : ""}…`}</option>
                  {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button className={`btn ${picked.length ? "btn-primary" : ""}`} onClick={toCampaign} disabled={!picked.length} data-testid="cm-campaign"><Share2 size={13} />Add to campaign{picked.length ? ` (${picked.length})` : ""}</button>
                <button className="btn btn-danger" onClick={() => setConfirm({ bulk: true })} disabled={!picked.length} title="Remove the selected LPs from this pipeline" data-testid="cm-bulk-delete"><Trash2 size={13} />Delete{picked.length ? ` (${picked.length})` : ""}</button>
              </div>
              <div className="cm-legend" data-testid="cm-legend">
                <button type="button" className={`cm-legend-chip ${eng === "campaign" ? "on" : ""}`} onClick={() => setEng(eng === "campaign" ? "" : "campaign")} title="Show only LPs already in a campaign" data-testid="cm-filter-campaign">
                  <span className="lead-name-dot campaign" style={{ position: "static" }} /> In a campaign{eng === "campaign" ? " ✓" : ""}
                </button>
                <button type="button" className={`cm-legend-chip ${eng === "hot" ? "on" : ""}`} onClick={() => setEng(eng === "hot" ? "" : "hot")} title="Show only hot leads" data-testid="cm-filter-hot">
                  <span className="lead-name-dot" style={{ position: "static", background: "#e05a5a", boxShadow: "0 0 8px rgba(224,90,90,0.7)" }} /> Hot lead{eng === "hot" ? " ✓" : ""}
                </button>
                <button type="button" className={`cm-legend-chip ${eng === "new" ? "on" : ""}`} onClick={() => setEng(eng === "new" ? "" : "new")} title="Show only LPs not yet in a campaign" data-testid="cm-filter-new">
                  Not in a campaign{eng === "new" ? " ✓" : ""}
                </button>
              </div>

              <div className="cm-table-wrap">
                <table className="tbl inv-tbl" data-testid="cm-table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" checked={allChecked} onChange={(e) => setSel(e.target.checked ? Object.fromEntries(page.map((l) => [l.id, true])) : {})} data-testid="cm-check-all" /></th>
                      <th>LP</th><th>Email</th><th>Firm</th><th>Type</th><th>Stage</th><th>Interest</th>
                      <th className="num">Indicated</th><th className="num">Committed</th>
                      <th>Next follow-up</th><th>Last contact</th><th>Source</th><th /><th /><th />
                    </tr>
                  </thead>
                  <tbody>
                    {page.map((l) => (
                      <LeadRow key={l.id} lead={l} today={today} checked={!!sel[l.id]} expanded={expandedId === l.id}
                        onCheck={() => setSel((x) => ({ ...x, [l.id]: !x[l.id] }))}
                        onExpand={(id) => setExpandedId(expandedId === id ? null : id)}
                        onMail={(id) => setExpandedId(id)}
                        onChange={replace} onEdit={(x) => setModal({ open: true, initial: x })} onRemove={(x) => setConfirm(x)} />
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <div className="cm-empty-block" data-testid="cm-empty">
                    No LPs here yet. Add one after your next meeting, import your Investors CRM, or sync soft-commits from the Deal Room.
                  </div>
                )}
                {filtered.length > shown && (
                  <button className="btn cm-more" onClick={() => setShown(shown + PAGE)} data-testid="cm-load-more">Show {Math.min(PAGE, filtered.length - shown)} more of {filtered.length}</button>
                )}
              </div>
            </Card>
          )}

          {sel_ === "pipeline" && expandedLead && (
            <LeadDetail lead={expandedLead} today={today} onChange={replace} onClose={() => setExpandedId(null)} />
          )}
        </div>
      </div>}

      <LeadModal open={modal.open} initial={modal.initial} dealKey={dealKey} dealName={platformName}
        onClose={() => setModal({ open: false, initial: null })} onSave={save} />
      <ConfirmModal open={!!confirm} title={confirm?.bulk ? "Remove selected LPs" : "Remove LP"} danger confirmLabel="Remove"
        message={confirm ? (confirm.bulk ? `Remove ${picked.length} selected LP(s) from the fundraising pipeline? Activity history will be deleted.` : `Remove ${confirm.name} from the fundraising pipeline? Activity history will be deleted.`) : ""}
        onConfirm={() => { (confirm.bulk ? bulkDelete() : remove(confirm)); setConfirm(null); }} onCancel={() => setConfirm(null)} />
      <AddToCampaignModal open={outreach.open} onClose={() => { outreach.setOpen(false); load(); }} contacts={outreach.contacts} defaultGroupName={outreach.defaultName} source="capital" dealKey={dealKey} dealName={platformName} />
      <ComposeEmailModal open={compose.open} onClose={() => compose.setOpen(false)} config={compose.config} />
    </div>
  );
}
